import React, { useState, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  VStack,
  Text,
  HStack,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  SearchIcon,
  CloseIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';
import { Message } from '../../services/chatService';

interface ChatSearchProps {
  messages: Message[];
  onResultClick?: (index: number) => void;
  isOpen: boolean;
  onClose: () => void;
  onQueryChange?: (query: string) => void;
}

const ChatSearch: React.FC<ChatSearchProps> = ({
  messages,
  onResultClick,
  isOpen,
  onClose,
  onQueryChange,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ message: Message; index: number }[]>(
    []
  );
  const [currentResultIndex, setCurrentResultIndex] = useState(0);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      const found = messages
        .map((message, index) => ({ message, index }))
        .filter(({ message }) =>
          message.content.toLowerCase().includes(lowerQuery)
        );
      setResults(found);
      setCurrentResultIndex(0);
    } else {
      setResults([]);
      setCurrentResultIndex(0);
    }
    // Notify parent about query change
    if (onQueryChange) {
      onQueryChange(query.trim());
    }
  }, [query, messages, onQueryChange]);

  const handleNext = () => {
    if (results.length > 0) {
      const nextIndex = (currentResultIndex + 1) % results.length;
      setCurrentResultIndex(nextIndex);
      if (onResultClick) {
        onResultClick(results[nextIndex].index);
      }
    }
  };

  const handlePrevious = () => {
    if (results.length > 0) {
      const prevIndex =
        currentResultIndex === 0 ? results.length - 1 : currentResultIndex - 1;
      setCurrentResultIndex(prevIndex);
      if (onResultClick) {
        onResultClick(results[prevIndex].index);
      }
    }
  };

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setCurrentResultIndex(0);
    if (onQueryChange) {
      onQueryChange('');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Box
      p={3}
      bg={bgColor}
      borderBottom='1px'
      borderColor={borderColor}
      position='relative'
      zIndex={10}
    >
      <VStack spacing={2} align='stretch'>
        <HStack>
          <InputGroup size='sm'>
            <InputLeftElement pointerEvents='none'>
              <SearchIcon color='gray.400' />
            </InputLeftElement>
            <Input
              placeholder='Search messages...'
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
              borderRadius='full'
            />
            <InputRightElement>
              <IconButton
                aria-label='Close search'
                icon={<CloseIcon />}
                size='xs'
                variant='ghost'
                onClick={handleClose}
              />
            </InputRightElement>
          </InputGroup>

          {results.length > 0 && (
            <HStack spacing={1}>
              <Badge colorScheme='primary' fontSize='xs'>
                {currentResultIndex + 1} of {results.length}
              </Badge>
              <IconButton
                aria-label='Previous result'
                icon={<ChevronUpIcon />}
                size='xs'
                onClick={handlePrevious}
                isDisabled={results.length === 0}
              />
              <IconButton
                aria-label='Next result'
                icon={<ChevronDownIcon />}
                size='xs'
                onClick={handleNext}
                isDisabled={results.length === 0}
              />
            </HStack>
          )}
        </HStack>

        {query && results.length === 0 && (
          <Text fontSize='xs' color='gray.500' textAlign='center'>
            No results found
          </Text>
        )}
      </VStack>
    </Box>
  );
};

export default ChatSearch;
